import styled, { css } from 'styled-components/macro';
import { KindColor } from './textBoxesModel';

const RGB: Record<KindColor, string> = {
  red: '220, 38, 38',
  teal: '13, 148, 136',
  purple: '124, 58, 237',
  magenta: '192, 38, 211',
  neutral: '107, 114, 128'
};

const INK: Record<KindColor, string> = {
  red: '#9A1B1B',
  teal: '#09685F',
  purple: '#5729A6',
  magenta: '#861B94',
  neutral: '#4B505A'
};

const NORMAL_BORDER = 'rgba(59, 111, 216, 0.55)';
const MONO = '"Courier New", Courier, monospace';
const SANS = 'Arial, Helvetica, sans-serif';

export const kindRgb = (color: KindColor, alpha: number) => `rgba(${RGB[color]}, ${alpha})`;

export const TextBoxesHead = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  margin: -16px -20px 12px;
  padding: 12px 20px 10px;
  background: white;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
`;

export const TextBoxesControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 10px;
`;

export const TextBoxesSpacer = styled.div`
  flex: 1;
`;

export const ImageSwitch = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[700]};
  cursor: pointer;
  user-select: none;

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  i {
    position: relative;
    width: 30px;
    height: 17px;
    border-radius: 999px;
    background: ${({ theme }) => theme.color.gray[250]};
  }

  i::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s;
  }

  input:checked + i {
    background: ${({ theme }) => theme.color.gray[900]};
  }

  input:checked + i::after {
    transform: translateX(13px);
  }

  input:focus-visible + i {
    outline: 2px solid ${({ theme }) => theme.color.blue[600]};
    outline-offset: 2px;
  }
`;

export const Pager = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.color.gray[700]};

  span {
    min-width: 7.5em;
    text-align: center;
  }

  em {
    margin-right: 6px;
    font-style: normal;
    font-size: 12px;
    color: ${({ theme }) => theme.color.gray[400]};
  }

  button {
    width: 28px;
    height: 28px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.color.gray[250]};
    background: white;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.gray[700]};
`;

export const LegendItem = styled.span<{ $color: KindColor | null }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  > span {
    display: flex;
    flex-direction: column;
    line-height: 1.35;
  }

  b {
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[900]};
  }

  em {
    margin-left: 4px;
    font-style: normal;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${({ $color }) => ($color ? kindRgb($color, 1) : 'inherit')};
  }

  small {
    font-size: 11.5px;
    color: ${({ theme }) => theme.color.gray[400]};
  }
`;

const chipFlag = css<{ $color: KindColor | null; $dashed?: boolean }>`
  ${({ $color, $dashed }) =>
    $color &&
    css`
      border: 1.5px ${$dashed ? 'dashed' : 'solid'} ${kindRgb($color, 1)};
      background: ${$dashed
        ? `repeating-linear-gradient(135deg, white 0 3px, ${kindRgb($color, 0.16)} 3px 6px)`
        : kindRgb($color, 0.12)};
      color: ${INK[$color]};
    `}
`;

export const Chip = styled.i<{
  $color: KindColor | null;
  $font: 'mono' | 'sans';
  $dashed?: boolean;
  $blank?: boolean;
}>`
  display: inline-block;
  min-width: 22px;
  min-height: 16px;
  padding: 0 4px;
  font-style: normal;
  font-family: ${({ $font }) => ($font === 'mono' ? MONO : SANS)};
  font-size: 12.5px;
  line-height: 18px;
  white-space: pre;
  border: 1px solid rgba(59, 111, 216, 0.5);
  border-radius: 2px;
  background: rgba(59, 111, 216, 0.06);
  color: ${({ $blank, theme }) => ($blank ? theme.color.gray[300] : '#1f2937')};
  ${chipFlag}
`;

export const OrphanChip = styled(Chip)`
  font-family: inherit;
  font-style: italic;
  font-size: 11.5px;
`;

export const Pages = styled.div`
  display: grid;
  gap: 6px;
`;

export const PageLabel = styled.div`
  width: 100%;
  max-width: 1000px;
  margin: 12px auto 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.gray[400]};

  &:first-child {
    margin-top: 0;
  }
`;

export const PageFrame = styled.div<{ $ratio: number; $showText: boolean }>`
  position: relative;
  container-type: inline-size;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  aspect-ratio: ${({ $ratio }) => $ratio};
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  canvas {
    visibility: ${({ $showText }) => ($showText ? 'hidden' : 'visible')};
  }
`;

export const PageCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
`;

export const Box = styled.span<{
  $color: KindColor | null;
  $font: 'mono' | 'sans';
  $showText: boolean;
  $dashed?: boolean;
}>`
  position: absolute;
  z-index: ${({ $color }) => ($color ? 1 : 0)};
  font-family: ${({ $font }) => ($font === 'mono' ? MONO : SANS)};
  line-height: 1.05;
  white-space: pre;
  overflow: hidden;
  border-radius: 2px;
  border: ${({ $color, $dashed }) =>
    $color
      ? `1.5px ${$dashed ? 'dashed' : 'solid'} ${kindRgb($color, 1)}`
      : `1px solid ${NORMAL_BORDER}`};
  background: ${({ $color, $dashed }) =>
    $color ? kindRgb($color, $dashed ? 0.08 : 0.16) : 'transparent'};
  color: ${({ $showText, $color }) => {
    if (!$showText) return 'transparent';
    return $color ? INK[$color] : '#1f2937';
  }};

  &:hover {
    outline: 2px solid ${({ $color }) => ($color ? kindRgb($color, 1) : NORMAL_BORDER)};
    outline-offset: 1px;
  }
`;

export const Rows = styled.div`
  display: grid;
  gap: 4px;
`;

export const RowCard = styled.div<{ $color: KindColor | null }>`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 5px 10px;
  border-radius: 6px;
  background: white;
  border: 1px solid
    ${({ $color, theme }) => ($color ? kindRgb($color, 0.45) : theme.color.gray[150])};
  box-shadow: ${({ $color }) => ($color ? `inset 3px 0 0 ${kindRgb($color, 1)}` : 'none')};
`;

export const RowNote = styled.span<{ $color: KindColor; $kind: 'gap' | 'step' }>`
  display: inline-flex;
  align-items: center;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $color }) => kindRgb($color, 1)};
  ${({ $kind, $color }) =>
    $kind === 'gap'
      ? css`
          border: 1.5px dashed ${kindRgb($color, 1)};
          border-radius: 2px;
        `
      : css`
          border-bottom: 2px solid ${kindRgb($color, 1)};
        `}
`;

export const AlsoFound = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin-top: 14px;
  padding-top: 12px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.gray[700]};
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};

  > b {
    flex-basis: 100%;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.gray[400]};
  }
`;

export const Tooltip = styled.div`
  position: fixed;
  z-index: 1000;
  max-width: 280px;
  padding: 7px 10px;
  border-radius: 6px;
  background: #1a202c;
  color: white;
  font-size: 12px;
  line-height: 1.4;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  > div + div {
    margin-top: 7px;
    padding-top: 7px;
    border-top: 1px solid #4a5568;
  }

  b {
    display: block;
    font-weight: 600;
  }

  span {
    display: block;
    color: #cbd5e0;
  }

  code {
    display: block;
    margin-top: 3px;
    font-family: ${MONO};
    white-space: pre-wrap;
  }
`;

export const PaneNotice = styled.p`
  margin: 0 0 12px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.gray[400]};
`;
